import type { HealthAttributes } from '@adtrackify/at-tracking-event-types';

import { EMQ_AGGREGATED_STATUS } from '@/features/workspace/types/use-emq-health-status-enums';
import { AGGREGATED_HEALTH_STATUS } from '@/features/workspace/types/use-integration-health-status-enums';

import { NAV_STATUS_KEY, NAV_STATUS_VARIANT } from '@/lib/navigation/types/nav-enums';
import type { NavStatusSnapshot } from '@/lib/navigation/types/nav-types';

export function getStatusVariant(
  status: AGGREGATED_HEALTH_STATUS
): NAV_STATUS_VARIANT {
  switch (status) {
    case AGGREGATED_HEALTH_STATUS.UNHEALTHY:
      return NAV_STATUS_VARIANT.ERROR;
    case AGGREGATED_HEALTH_STATUS.UNKNOWN:
    case AGGREGATED_HEALTH_STATUS.NOT_CONNECTED:
      return NAV_STATUS_VARIANT.WARNING;
    case AGGREGATED_HEALTH_STATUS.HEALTHY:
    default:
      return NAV_STATUS_VARIANT.SUCCESS;
  }
}

export function resolveEventHealthStatus(
  attributes?: HealthAttributes
): AGGREGATED_HEALTH_STATUS {
  if (!attributes) {
    return AGGREGATED_HEALTH_STATUS.NOT_CONNECTED;
  }

  const isUnhealthy =
    !attributes.themeAppExtensionHealthy ||
    !attributes.conversionPixelHealthy ||
    !attributes.customPixelHealthy;

  if (isUnhealthy) {
    return AGGREGATED_HEALTH_STATUS.UNHEALTHY;
  }

  return AGGREGATED_HEALTH_STATUS.HEALTHY;
}

export function getEmqStatusVariant(status: EMQ_AGGREGATED_STATUS): NAV_STATUS_VARIANT {
  switch (status) {
    case EMQ_AGGREGATED_STATUS.CRITICAL:
      return NAV_STATUS_VARIANT.ERROR;
    case EMQ_AGGREGATED_STATUS.WARNING:
      return NAV_STATUS_VARIANT.WARNING;
    case EMQ_AGGREGATED_STATUS.HEALTHY:
    case EMQ_AGGREGATED_STATUS.NOT_CONFIGURED:
    default:
      return NAV_STATUS_VARIANT.SUCCESS;
  }
}

export function getStatusAccessibleLabel(variant: NAV_STATUS_VARIANT): string {
  switch (variant) {
    case NAV_STATUS_VARIANT.ERROR:
      return 'Status: error';
    case NAV_STATUS_VARIANT.WARNING:
      return 'Status: warning';
    case NAV_STATUS_VARIANT.PENDING:
      return 'Status: checking';
    case NAV_STATUS_VARIANT.SUCCESS:
    default:
      return 'Status: healthy';
  }
}

export function formatUnreadCountLabel(count: number): string {
  if (count <= 0) {
    return '';
  }
  const display = count > 9 ? '9+' : String(count);
  return `${display} unread notifications`;
}

export function aggregateControlPanelStatus(
  snapshot: NavStatusSnapshot
): NAV_STATUS_VARIANT {
  const variants = [
    snapshot.variants[NAV_STATUS_KEY.INTEGRATION_HEALTH],
    snapshot.variants[NAV_STATUS_KEY.DESTINATION_HEALTH],
    snapshot.variants[NAV_STATUS_KEY.EVENT_HEALTH],
    snapshot.variants[NAV_STATUS_KEY.EMQ_HEALTH],
  ].filter((value): value is NAV_STATUS_VARIANT => value !== undefined);

  if (variants.includes(NAV_STATUS_VARIANT.ERROR)) {
    return NAV_STATUS_VARIANT.ERROR;
  }
  if (variants.includes(NAV_STATUS_VARIANT.WARNING)) {
    return NAV_STATUS_VARIANT.WARNING;
  }
  if (variants.includes(NAV_STATUS_VARIANT.PENDING)) {
    return NAV_STATUS_VARIANT.PENDING;
  }
  return NAV_STATUS_VARIANT.SUCCESS;
}

export function buildNavStatusSnapshot(input: {
  integrationStatus: AGGREGATED_HEALTH_STATUS;
  integrationLoading: boolean;
  destinationStatus: AGGREGATED_HEALTH_STATUS;
  eventHealthStatus: AGGREGATED_HEALTH_STATUS;
  eventHealthLoading: boolean;
  emqStatus: EMQ_AGGREGATED_STATUS;
  emqLoading: boolean;
  unreadCount?: number;
}): NavStatusSnapshot {
  return {
    variants: {
      [NAV_STATUS_KEY.INTEGRATION_HEALTH]: input.integrationLoading
        ? NAV_STATUS_VARIANT.PENDING
        : getStatusVariant(input.integrationStatus),
      [NAV_STATUS_KEY.DESTINATION_HEALTH]: getStatusVariant(input.destinationStatus),
      [NAV_STATUS_KEY.EVENT_HEALTH]: input.eventHealthLoading
        ? NAV_STATUS_VARIANT.PENDING
        : getStatusVariant(input.eventHealthStatus),
      [NAV_STATUS_KEY.EMQ_HEALTH]: input.emqLoading
        ? NAV_STATUS_VARIANT.PENDING
        : getEmqStatusVariant(input.emqStatus),
      [NAV_STATUS_KEY.UNREAD_NOTIFICATIONS]:
        (input.unreadCount ?? 0) > 0
          ? NAV_STATUS_VARIANT.WARNING
          : NAV_STATUS_VARIANT.SUCCESS,
    },
    unreadCount: input.unreadCount,
  };
}
